import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import BranchSelector from './BranchSelector';
import { Button } from '../ui/button';
import { toast } from '../../lib/toast';
import {
  ChefHat,
  LayoutDashboard,
  ShoppingCart,
  Grid3X3,
  Package,
  Package2,
  FlaskConical,
  ShoppingBag,
  Users,
  UsersRound,
  Boxes,
  Wallet,
  BarChart3,
  Brain,
  Settings,
  LogOut,
  Menu,
  X,
  UtensilsCrossed,
  UserCircle,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Truck,
  FolderTree,
  Shield,
  Receipt,
  Factory,
  Star,
  FileText,
  ShieldAlert,
  TrendingUp,
  Building2,
  Target,
  Sparkles,
  Gift,
  QrCode,
  Megaphone,
  Layers,
  Bot
} from 'lucide-react';

// Role permissions mapping
const ROLE_PERMISSIONS = {
  admin: ['*'],
  administrador: ['*'],
  gerente: ['dashboard', 'pos', 'orders', 'cash_register', 'inventory', 'reports', 'customers', 'suppliers', 'products', 'kitchen', 'tables', 'supplies', 'purchases'],
  cajero: ['dashboard', 'pos', 'orders', 'cash_register'],
  mesero: ['pos', 'orders', 'tables'],
  cocinero: ['dashboard', 'kitchen', 'orders'],
  almacenista: ['dashboard', 'inventory', 'supplies', 'purchases']
};

// Route to permission mapping
const ROUTE_PERMISSIONS = {
  '/dashboard': ['dashboard'],
  '/pos': ['pos'],
  '/kitchen-monitor': ['kitchen'],
  '/tables': ['tables'],
  '/cash-register': ['cash_register'],
  '/supplies': ['supplies'],
  '/processed-supplies': ['supplies'],
  '/purchases': ['purchases'],
  '/inventory': ['inventory'],
  '/kardex': ['inventory'],
  '/products': ['products'],
  '/suppliers': ['suppliers'],
  '/customers': ['customers'],
  '/reports': ['reports'],
  '/ai-insights': ['reports'],
  '/settings': ['settings'],
  '/users': ['users']
};

// Menu structure organized by modules
const menuStructure = [
  {
    id: 'dashboard',
    type: 'single',
    path: '/dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    permissions: ['dashboard']
  },
  {
    id: 'ventas',
    type: 'group',
    icon: DollarSign,
    label: 'Ventas',
    items: [
      { path: '/pos', icon: ShoppingCart, label: 'Terminal POS', permissions: ['pos'] },
      { path: '/kitchen-monitor', icon: UtensilsCrossed, label: 'Monitor Cocina', permissions: ['kitchen'] },
      { path: '/tables', icon: Grid3X3, label: 'Mesas', permissions: ['tables'] },
      { path: '/cash-register', icon: Wallet, label: 'Control de Caja', permissions: ['cash_register'] },
    ]
  },
  {
    id: 'inventario',
    type: 'group',
    icon: Boxes,
    label: 'Inventario',
    items: [
      { path: '/supplies', icon: Package2, label: 'Insumos', permissions: ['supplies'] },
      { path: '/processed-supplies', icon: FlaskConical, label: 'Elaborados', permissions: ['supplies'] },
      { path: '/production', icon: Factory, label: 'Producción', permissions: ['inventory'] },
      { path: '/purchases', icon: ShoppingBag, label: 'Compras', permissions: ['purchases'] },
      { path: '/inventory', icon: Boxes, label: 'Stock por Área', permissions: ['inventory'] },
      { path: '/kardex', icon: FileText, label: 'Kardex', permissions: ['inventory'] },
      { path: '/inventory-adjustments', icon: ShieldAlert, label: 'Ajustes', permissions: ['inventory'] },
    ]
  },
  {
    id: 'catalogo',
    type: 'group',
    icon: FolderTree,
    label: 'Catálogo',
    items: [
      { path: '/products', icon: Package, label: 'Productos', permissions: ['products'] },
      { path: '/extras', icon: Layers, label: 'Extras', permissions: ['products'] },
      { path: '/special-products', icon: Star, label: 'Productos Especiales', permissions: ['products'] },
    ]
  },
  {
    id: 'operaciones',
    type: 'group',
    icon: Truck,
    label: 'Operaciones',
    items: [
      { path: '/suppliers', icon: Users, label: 'Proveedores', permissions: ['suppliers'] },
      { path: '/customers', icon: UserCircle, label: 'Clientes', permissions: ['customers'] },
      { path: '/crm-inteligente', icon: Sparkles, label: 'CRM Inteligente', permissions: ['reports'], highlight: true },
      { path: '/crm-domicilio', icon: Target, label: 'CRM Domicilio', permissions: ['reports'] },
      { path: '/programa-lealtad', icon: Gift, label: 'Programa Lealtad', permissions: ['reports'], highlight: true },
      { path: '/menu-admin', icon: QrCode, label: 'Menú Digital', permissions: ['reports'], highlight: true },
      { path: '/promo-studio', icon: Megaphone, label: 'Promo Studio', permissions: ['reports'], highlight: true },
      { path: '/egresos', icon: Receipt, label: 'Gastos Operativos', permissions: ['cash_register'] },
    ]
  },
  {
    id: 'admin',
    type: 'group',
    icon: Shield,
    label: 'Administración',
    adminOnly: true,
    items: [
      { path: '/users', icon: UsersRound, label: 'Usuarios y Roles', permissions: ['users'] },
    ]
  },
  {
    id: 'reportes',
    type: 'group',
    icon: BarChart3,
    label: 'Reportes',
    items: [
      { path: '/reports-hub', icon: BarChart3, label: 'Centro de Reportes', permissions: ['reports'] },
      { path: '/sales-comparison', icon: TrendingUp, label: 'Comparativo Ventas', permissions: ['reports'] },
      { path: '/area-consumption', icon: Boxes, label: 'Consumo por Área', permissions: ['reports'] },
      { path: '/sales-report', icon: Receipt, label: 'Ventas Detalladas', permissions: ['reports'] },
      { path: '/financial-reports', icon: DollarSign, label: 'Reportes Financieros', permissions: ['reports'] },
      { path: '/reports', icon: BarChart3, label: 'Analíticas', permissions: ['reports'] },
      { path: '/ai-insights', icon: Brain, label: 'IA Insights', permissions: ['reports'] },
    ]
  },
  {
    id: 'configuracion',
    type: 'single',
    path: '/settings',
    icon: Settings,
    label: 'Configuración',
    adminOnly: true,
    permissions: ['settings']
  },
  {
    id: 'sucursales',
    type: 'single',
    path: '/branches',
    icon: Building2,
    label: 'Sucursales',
    adminOnly: true,
    permissions: ['settings']
  },
  {
    id: 'distribucion',
    type: 'single',
    path: '/distribution',
    icon: Truck,
    label: 'Distribución',
    adminOnly: true,
    matrizOnly: true,  // Solo visible para usuarios de Matriz
    permissions: ['inventario']
  },
  {
    id: 'reportes-sucursales',
    type: 'single',
    path: '/branch-reports',
    icon: BarChart3,
    label: 'Reportes Sucursales',
    adminOnly: false,
    permissions: ['reports']
  },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['ventas', 'inventario']); // Default expanded

  // Get user role and permissions
  const userRole = user?.role || 'mesero';
  const userPermissions = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.mesero;
  const isAdmin = userRole === 'admin' || userRole === 'administrador';

  // Check if user has permission for a route
  const hasPermission = (requiredPermissions) => {
    if (isAdmin || userPermissions.includes('*')) return true;
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.some(perm => userPermissions.includes(perm));
  };

  // Check if user is from Matriz (no branch_id assigned)
  const isMatrizUser = !user?.branch_id;

  // Filter menu items based on user permissions
  const filteredMenu = useMemo(() => {
    return menuStructure
      .filter(item => {
        // Admin-only items
        if (item.adminOnly && !isAdmin) return false;
        
        // Matriz-only items (only visible to users without branch_id)
        if (item.matrizOnly && !isMatrizUser) return false;
        
        // Single items
        if (item.type === 'single') {
          return hasPermission(item.permissions);
        }
        
        // Groups - filter their items
        return true;
      })
      .map(item => {
        if (item.type === 'group') {
          const filteredItems = item.items.filter(subItem => 
            hasPermission(subItem.permissions)
          );
          // Only include group if it has visible items
          if (filteredItems.length === 0) return null;
          return { ...item, items: filteredItems };
        }
        return item;
      })
      .filter(Boolean);
  }, [userRole, isAdmin, isMatrizUser]);

  // Get role display name
  const getRoleName = (role) => {
    const names = {
      admin: 'Administrador',
      gerente: 'Gerente',
      cajero: 'Cajero',
      mesero: 'Mesero',
      cocinero: 'Cocinero',
      almacenista: 'Almacenista'
    };
    return names[role] || 'Usuario';
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const isPathActive = (path) => location.pathname === path;
  
  const isGroupActive = (items) => items?.some(item => location.pathname === item.path);

  const NavLink = ({ item, isNested = false }) => {
    const isActive = isPathActive(item.path);
    return (
      <Link
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
          isNested ? 'ml-4 text-sm' : ''
        } ${
          isActive 
            ? 'bg-accent text-white shadow-md shadow-accent/20' 
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <item.icon className={`${isNested ? 'w-4 h-4' : 'w-5 h-5'}`} />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  const MenuGroup = ({ group }) => {
    const isExpanded = expandedGroups.includes(group.id);
    const hasActiveChild = isGroupActive(group.items);
    
    return (
      <div className="space-y-1">
        {/* Group Header */}
        <button
          onClick={() => toggleGroup(group.id)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all ${
            hasActiveChild 
              ? 'bg-accent/10 text-accent' 
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          data-testid={`nav-group-${group.id}`}
        >
          <div className="flex items-center gap-3">
            <group.icon className="w-5 h-5" />
            <span className="font-semibold text-sm uppercase tracking-wide">{group.label}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        
        {/* Group Items */}
        {isExpanded && (
          <div className="space-y-1 border-l-2 border-muted ml-6 pl-2">
            {group.items.map((item) => (
              <NavLink key={item.path} item={item} isNested />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Header - Professional style */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-border shadow-sm px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-md">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="font-chivo font-extrabold text-lg">SmartPOS</span>
          </Link>
          <Button 
            variant="ghost" 
            size="icon"
            className="h-10 w-10"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 md:w-64 bg-card border-r border-border z-50
        transform transition-transform duration-300
        md:translate-x-0
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full p-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 mb-4 mt-2">
            <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <span className="font-chivo font-extrabold text-xl">SmartPOS</span>
          </Link>

          {/* Branch Selector */}
          <div className="mb-4">
            <BranchSelector />
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2 overflow-y-auto pr-1">
            {filteredMenu.map((item) => (
              item.type === 'single' ? (
                <NavLink key={item.id} item={item} />
              ) : (
                <MenuGroup key={item.id} group={item} />
              )
            ))}
          </nav>

          {/* User Section */}
          <div className="border-t border-border pt-4 mt-4">
            <div className="flex items-center gap-3 px-4 py-2 mb-2">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <span className="text-accent font-bold">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name || 'Usuario'}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {getRoleName(userRole)} • {user?.restaurant_name || 'Mi Restaurante'}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Padding */}
      <div className="md:hidden h-16" />
    </>
  );
};

export default Sidebar;
